<template>
    <div>
        <vs-table
            v-bind="$attrs"
            ref="table"
            class="client-side-table"
            v-model="localValue"
            :search="search"
            :data="indexedData"
            :max-items="pageSize_x"
            :class="{ squished: localValue }"
            :hover-flat="hoverFlat"
            :pagination="paginate"
            :currentPage="current_page"
            @sort="onSort"
        >
            <template slot="header">
                <vs-select
                    v-if="!fixedPageSize && paginate"
                    autocomplete
                    class="selectExample mb-5"
                    label="Page Size"
                    v-model="pageSize_x"
                >
                    <vs-select-item
                        v-for="(item, index) in pageSizeOptions"
                        :key="index"
                        :value="item.value"
                        :text="item.text"
                    />
                </vs-select>
                <slot name="header-inner"> </slot>
                <slot name="header-filter"></slot>
            </template>

            <template slot="thead">
                <vs-th v-if="$scopedSlots['expand']"> </vs-th>
                <template v-for="(column, colIndex) in columns">
                    <vs-th
                        v-if="column.visible == undefined || column.visible == true"
                        :key="'head-' + colIndex"
                        :sort-key="column.sort"
                    >
                        <slot :name="`${column.title}_head`" :column="column" :col-index="colIndex">
                            <span :class="column.headerClass" class="w-full whitespace-nowrap">
                                {{ column.title }}
                            </span>
                        </slot>
                    </vs-th>
                </template>
            </template>

            <template slot-scope="{ data }">
                <transition v-for="(row, index) in data" :key="index" name="list-enter-up" appear>
                    <vs-tr
                        :key="getKey(row, index)"
                        :data="row"
                        :class="getRowClasses(row)"
                        :ref="row.xIndex"
                        :style="{
                            transitionDelay: index * 0.02 + 's'
                        }"
                    >
                        <slot name="row" :row="row">
                            <template v-for="(column, colIndex) in columns">
                                <vs-td
                                    v-if="column.visible == undefined || column.visible == true"
                                    :key="'col-' + colIndex"
                                    :style="column.style"
                                    :class="getClassesX(row, column)"
                                >
                                    <slot
                                        :name="column.name || column.title.replace(/\s/g, '')"
                                        :row="row"
                                        :col-index="colIndex"
                                        :row-index="index"
                                    >
                                        <span>{{ getValue(row, column) }}</span>
                                    </slot>
                                </vs-td>
                            </template>
                        </slot>
                        <template v-slot:expand>
                            <slot name="expand" :row="row"> </slot>
                        </template>
                    </vs-tr>
                </transition>
            </template>
        </vs-table>
        <vx-pagination v-if="paginate" class="mt-4" :total="totalPages" v-model="current_page" />
    </div>
</template>

<script>
export default {
    name: 'client-side-table',
    props: {
        value: {
            Type: Object
        },
        columns: {
            Type: Array,
            required: true
        },
        data: {
            Type: Array,
            required: true
        },
        search: {
            Type: Boolean,
            default: true
        },
        hoverFlat: {
            Type: Boolean,
            default: false
        },
        idKey: {
            type: [Function, String],
            required: true
        },
        rowClass: {
            type: [Function, String],
            default: ''
        },
        pageSize: {
            type: [Number, String],
            default: 15
        },
        paginate: {
            Type: Boolean,
            default: true
        },
        fixedPageSize: {
            Type: Boolean,
            default: false
        }
    },
    watch: {
        pageSize_x(val) {
            this.$refs.table._data.currentx = 1
            this.$emit('updated:pageSize', val)
        },
        data: {
            handler: function (newV) {
                this.$refs.table._data.currentx = 1
                this.$emit('page-results-changed', newV)
            },
            deep: true
        }
    },
    data() {
        return {
            pageSize_x: 15,
            sortKey: null,
            sortType: null,
            currentPage: 1,
            filterCount: null,
            // default value
            pageSizeOptions: [
                { text: '10', value: 10 },
                { text: '15', value: 15 },
                { text: '50', value: 50 },
                { text: '100', value: 100 },
                { text: '200', value: 200 }
            ]
        }
    },
    computed: {
        localValue: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },
        getKey() {
            if (typeof this.idKey === 'function') {
                return this.idKey
            }
            return (row, index) => {
                return row[this.idKey] || index
            }
        },
        indexedData() {
            if (!this.data || this.data.length == 0) {
                return []
            }
            return this.data.map(function (value, index) {
                return { ...value, xIndex: index + 1 }
            })
        },
        totalPages() {
            var current = this.filterCount || this.indexedData.length
            return 1 + parseInt(current / this.pageSize) || 1
        },
        current_page: {
            get() {
                return this.currentPage
            },
            set(val) {
                this.currentPage = val
            }
        },
        getRowClasses() {
            if (!this.rowClass) {
                return () => ''
            } else if (typeof this.rowClass === 'function') {
                return this.rowClass
            }
            return () => this.rowClass
        }
    },
    methods: {
        getClassesX(row, column) {
            if (!column.class) {
                return ''
            } else if (typeof column.class === 'string') {
                return column.class
            }

            return column.class(row)
        },
        getValue(row, column) {
            var val = ''
            try {
                if (!column.value) {
                    val = ''
                } else if (typeof column.value === 'function') {
                    val = column.value(row)
                } else if (typeof column.value === 'string') {
                    val = this.retrieveProperty(column.value, row)
                }

                if (column.format) {
                    return column.format(val)
                }
            } catch (ex) {
                console.error(ex)
            }
            return val
        },
        retrieveProperty(propString, obj) {
            return propString.split('.').reduce((o, i) => o[i], obj)
        },
        onSort(sortKey, sortType) {
            // if no sort type is passed, removed the sort key as it is no longer sorted on
            // this way it will y=use the original cached data set
            this.sortKey = sortType ? sortKey : null
            this.sortType = sortType
        }
    },
    mounted() {
        this.$watch('$refs.table.searchx', (newV, oldV) => this.$emit('search', newV))
        this.$watch('$refs.table.currentx', (newV, oldV) => this.$emit('change-page', newV))
        this.$watch(
            '$refs.table.queriedResults',
            (newV, oldV) => (this.filterCount = (newV && newV.length) || 0)
        )
    }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';
.client-side-table {
    height: calc(100% - 54px);

    ::v-deep .vs-table--pagination {
        display: none;
    }

    .vx-table-body {
        height: 100%;
        overflow: hidden;
    }

    ::v-deep .vs-table--content {
        flex-grow: 1;
        overflow: hidden !important;
    }

    ::v-deep thead,
    ::v-deep th {
        background-color: inherit;
        position: sticky;
        top: 0;
        z-index: 1;

        .vs-table-text {
            color: rgba(var(--vs-primary), 1) !important;
        }
    }

    ::v-deep .vs-table--tbody {
        overflow: auto;
    }

    &.has-data {
        ::v-deep .vs-table--tbody {
            height: 100%;
            min-height: 200px;
        }
    }
}
</style>
